import React, {
  memo,
  useMemo,
} from 'react';

import { Typography } from 'antd';
import moment from 'moment';
import {
  PiArrowDownRightBold,
  PiThumbsUp,
} from 'react-icons/pi';
import TMIcon from 'tm-icons-library';

import CustomPill from '../../../../components/CustomPill/CustomPill';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../context/ThemeProvider';
import { TIME_FORMAT } from '../../../../shared/constants';
import { DELETED_USER } from '../../../../shared/data';
import {
  checkIfCurrentLoggedInUser,
  getItemDisplayName,
  getReadableDateAndTimeFormat,
  getSingularOrPlural,
  getUserDisplayName,
} from '../../../../shared/utils';
import {
  ITQActivityLog,
} from '../../../../types/tqConnectTypes/service-requests';

const ICON_SIZE = 16;

const BUTTON_SIZE = 32;

interface Props {
  activity: ITQActivityLog;
  onClick?: () => void;
}

const RecentActivityCard: React.FC<Props> = ({ activity, onClick }) => {
  const { colors: COLORS } = useTheme();

  const {
    type: activityType,
    read,
    receiver,
    sender,
    serviceRequest,
    createdAt,
  } = activity;

  const {
    title: serviceRequestTitle,
    totalResponses,
    visibility,
  } = serviceRequest || {};

  const { type, icon, title } = useMemo(() => {
    switch (activityType) {
      case 'multiple-responses':
        return {
          type: 'info',
          icon: (
            <TMIcon name="pricetags" size={ICON_SIZE} color={COLORS.INFO_ALT} />
          ),
          title: (
            <>
              <Typography.Text strong>
                {checkIfCurrentLoggedInUser(receiver?._id)
                  ? `You`
                  : `${receiver?.name || DELETED_USER}`}
              </Typography.Text>{' '}
              received{' '}
              <Typography.Text strong>
                {totalResponses > 10 ? '10+' : totalResponses}{' '}
                {getSingularOrPlural(totalResponses, 'offer')}
              </Typography.Text>
            </>
          ),
        };
      case 'request-created':
        return {
          type: 'warning',
          icon: <PiArrowDownRightBold size={ICON_SIZE} color={COLORS.ICON} />,
          title: (
            <>
              <Typography.Text strong>
                {checkIfCurrentLoggedInUser(receiver?._id)
                  ? `You`
                  : `${receiver?.name || DELETED_USER}`}
              </Typography.Text>{' '}
              received a request from{' '}
              <Typography.Text strong>
                {checkIfCurrentLoggedInUser(sender?._id)
                  ? 'You'
                  : sender?.name || DELETED_USER}
              </Typography.Text>{' '}
              to help on{' '}
              <Typography.Text
                strong>{`"${getItemDisplayName(serviceRequestTitle)}"`}</Typography.Text>
            </>
          ),
        };
      case 'offer-created':
        return {
          type: 'warning',
          icon: <PiArrowDownRightBold size={ICON_SIZE} color={COLORS.ICON} />,
          title: (
            <>
              <Typography.Text strong>
                {checkIfCurrentLoggedInUser(sender?._id)
                  ? 'You'
                  : sender?.name || DELETED_USER}
              </Typography.Text>{' '}
              offered help on{' '}
              <Typography.Text
                strong>{`"${getItemDisplayName(serviceRequestTitle)}"`}</Typography.Text>
            </>
          ),
        };
      case 'offer-accepted': {
        // const receiverToShow = visibility === 'private' ? receiver : sender;
        // const senderToShow = visibility === 'private' ? sender : receiver;

        return {
          type: 'success',
          icon: (
            <TMIcon
              name="checkmark-circle"
              size={ICON_SIZE}
              color={COLORS.SUCCESS}
            />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Yeah! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(sender?._id, sender?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              accepted{' '}
              <Typography.Text strong>
                {getUserDisplayName(receiver?._id, receiver?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              {visibility === 'private' ? 'request' : 'offer'} for{' '}
              <Typography.Text strong>
                {`"${getItemDisplayName(serviceRequestTitle)}"`}
              </Typography.Text>
            </>
          ),
        };
      }
      case 'offer-rejected': {
        // const receiverToShow = visibility === 'private' ? receiver : sender;
        // const senderToShow = visibility === 'private' ? sender : receiver;

        return {
          type: 'error',
          icon: (
            <TMIcon name="close-circle" size={ICON_SIZE} color={COLORS.ERROR} />
          ),
          title: (
            <>
              {visibility === 'private' ? 'Sorry! ' : ''}
              <Typography.Text strong>
                {getUserDisplayName(sender?._id, sender?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              rejected{' '}
              <Typography.Text strong>
                {getUserDisplayName(receiver?._id, receiver?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'your',
                  suffix: "'s",
                })}
              </Typography.Text>{' '}
              {visibility === 'private' ? 'request' : 'offer'} for{' '}
              <Typography.Text strong>
                {`"${getItemDisplayName(serviceRequestTitle)}"`}
              </Typography.Text>
            </>
          ),
        };
      }
      case 'offer-reviewed':
        return {
          type: 'success',
          icon: <PiThumbsUp size={ICON_SIZE} color={COLORS.SUCCESS} />,
          title: (
            <>
              <Typography.Text strong>
                {getUserDisplayName(receiver?._id, receiver?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              recently received a feedback from{' '}
              <Typography.Text strong>
                {getUserDisplayName(sender?._id, sender?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'you',
                })}
              </Typography.Text>{' '}
              for completing{' '}
              <Typography.Text strong>
                {`"${getItemDisplayName(serviceRequestTitle)}"`}
              </Typography.Text>
            </>
          ),
        };
      case 'message-received':
        return {
          type: 'info',
          icon: (
            <TMIcon
              name="chatbubble"
              size={ICON_SIZE}
              color={COLORS.INFO_ALT}
            />
          ),
          title: (
            <>
              <Typography.Text strong>
                {getUserDisplayName(receiver?._id, receiver?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              recently received a message from{' '}
              <Typography.Text strong>
                {getUserDisplayName(sender?._id, sender?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'you',
                })}
              </Typography.Text>{' '}
              for{' '}
              <Typography.Text strong>
                {`"${getItemDisplayName(serviceRequestTitle)}"`}
              </Typography.Text>
            </>
          ),
        };
      default:
        return {};
    }
  }, [
    COLORS.ERROR,
    COLORS.ICON,
    COLORS.INFO_ALT,
    COLORS.SUCCESS,
    activityType,
    receiver,
    sender,
    serviceRequestTitle,
    totalResponses,
    visibility,
  ]);

  return (
    <div
      className={`activity__list__card ${read ? 'activity__list__card--read' : ''} ${onClick ? 'activity__list__card--clickable' : ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}>
      <ProfileItem
        title={title}
        subtitle={`${moment(createdAt).format(TIME_FORMAT)}, ${getReadableDateAndTimeFormat(createdAt)}`}
        avatar={
          <CustomPill
            shape="circle"
            text={icon}
            textSize={ICON_SIZE}
            width={BUTTON_SIZE}
            height={BUTTON_SIZE}
            bgColor={
              (type === 'error' && COLORS.ERROR100) ||
              (type === 'success' && COLORS.SUCCESS100) ||
              (type === 'info' && COLORS.INFO100) ||
              (type === 'warning' && COLORS.WARNING200) ||
              COLORS.SURFACE_PAGE
            }
            disableHoverEffect
          />
        }
        action={
          read ? undefined : <span className="activity__list__card__badge" />
        }
        titleStyle={{
          marginBottom: 4,
          fontSize: 13,
          fontWeight: 400,
          color: COLORS.TEXT1,
        }}
        subtitleStyle={{
          fontSize: 12,
          fontWeight: 400,
        }}
        titleProps={{
          ellipsis: false,
        }}
        style={{ alignItems: 'start' }}
      />
    </div>
  );
};

export default memo(RecentActivityCard);
